import Vue from 'vue'
import Vuex from 'vuex'
import VueResource from 'vue-resource'
import router from '@/router'
import utils from '@/utils'
import messagesMap from '@/store/messages-map'
import cartStatuses from '@/store/cart-statuses'

let apiHost = process.env.API_HOST
let clientId = process.env.CLIENT_ID

Vue.use(Vuex)
Vue.use(VueResource)

Vue.http.options.root = apiHost
Vue.http.interceptors.push((request, next) => {
  next(response => {
    if (response.status === 401) {
      request._response = response
      sessionStorage.removeItem('tkn')
      router.push({
        name: 'login',
        params: { nextUrl: router.options.routes[0] }
      })
    }
    if (response.status >= 400) {
      let errorMessage = 'unknown-error'
      if (response.body.error_description) {
        errorMessage = response.body.error_description
      } else if (response.body.error) {
        errorMessage = response.body.error
      } else if (response.body.detail) {
        errorMessage = response.body.detail
      }
      this.a.dispatch('aMessageDisplay', errorMessage)
    }
    return response
  })
})

export default new Vuex.Store({
  state: {
    activeMenuIndex: null,
    account: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    addresses: [],
    apiEndpoints: {
      // api context:
      'account': 'users/accounts/details/',
      'addresses': 'users/addresses/',
      'compounds': 'compounds/',
      'compounds-categories': 'compounds/categories/',
      'compounds-consistencies': 'compounds/consistencies/',
      'compounds-informations': 'compounds/informations/',
      'compounds-purities': 'compounds/purities/',
      'compounds-units': 'compounds/units/',
      'content-articles': 'content/articles/',
      'cart-items': 'carts/items/',
      'cart-messages': 'carts/messages/',
      'cart-total': 'carts/total/',
      'carts': 'carts/',
      'intouch-messages': 'intouch/messages/',
      'user-accounts': 'users/accounts/',
      'user-accounts-password-reset': 'users/accounts/reset/',
      // auth context:
      'login': 'token/',
      // pdf context:
      'pdf-cart-offer': 'market/offer/'
    },
    carts: [],
    cart: {
      created: null,
      itemCount: 0,
      isOffer: false,
      name: null,
      price: 0,
      status: 'opened',
      statuses: [],
      compounds: []
    },
    cartItems: [],
    cartMessages: [],
    cartStatuses,
    categoryMap: {},
    contentMap: {},
    contentSlugMap: {},
    compounds: [], // price list
    compoundsMap: {
      categories: {},
      consistencies: {},
      informations: {},
      purities: {},
      units: {}
    },
    messageBase: {
      message: '',
      type: 'info',
      duration: 7000,
      showClose: true,
      center: false
    },
    messagesMap,
    pageTitle: 'B&K',
    paginationState: {
      currenPage: 1,
      page: 1
    },
    q: '',
    token: null
  },
  actions: {
    // accounts
    aFetchAccount ({ commit, getters }) {
      return this._vm.$http.get(getters.getApiUrl('account'), {
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetAccount', result)
        })
        .catch(() => {
          this.dispatch('aMessageDisplay', 'error-receive-addresses')
        })
    },
    aUserAccountChange ({ commit }, account) {
      const data = {
        first_name: account.firstName,
        last_name: account.lastName,
        email: account.email,
        phone: account.phone
      }
      return this._vm.$http.patch(this.getters.getApiUrl('account'), data, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          this.dispatch('aMessageDisplay', 'user-account-change-success')
        })
        .catch({})
    },
    aUserAccountCreate ({ commit }, account) {
      const data = {
        first_name: account.firstName,
        last_name: account.lastName,
        email: account.email,
        password: account.password
      }
      this._vm.$http.post(this.getters.getApiUrl('user-accounts'), data, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          router.push({ name: 'login' })
        })
    },
    aUserAccountPasswordReset ({ commit }, data) {
      this._vm.$http.post(this.getters.getApiUrl('user-accounts-password-reset'), data, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => {
          this.dispatch('aMessageDisplay', 'user-account-password-reset-done')
        })
        .catch({})
    },
    aUserAccountPasswordResetRequest ({ commit }, email) {
      const data = { email }
      this._vm.$http.post(this.getters.getApiUrl('user-accounts-password-reset'), data, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => {
          this.dispatch('aMessageDisplay', 'user-account-password-reset-request')
        })
    },

    // addresses
    aFetchAddresses ({ commit, getters }) {
      return this._vm.$http.get(getters.getApiUrl('addresses'), {
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetAddresses', result.data)
        })
        .catch(() => {
          this.dispatch('aMessageDisplay', 'error-receive-addresses')
        })
    },
    aAddressChange ({ commit }, address) {
      const data = {
        name: address.name,
        street: address.street,
        number: address.number,
        apartment: address.apartment,
        place: address.place,
        zip: address.zip,
        tax_number: address.taxNumber,
        phone: address.phone,
        default_shipment: address.defaultShipment,
        invoice_buyer: address.invoiceBuyer,
        invoice_recipient: address.invoiceRecipient
      }
      return this._vm.$http.patch(this.getters.getApiUrl('addresses', { obj: address.addressId }), data, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          this.dispatch('aMessageDisplay', 'user-address-change-success')
        })
        .catch({})
    },
    aAddressCreate ({ commit }, address) {
      const data = {
        name: address.name,
        street: address.street,
        number: address.number,
        apartment: address.apartment,
        place: address.place,
        zip: address.zip,
        tax_number: address.taxNumber,
        phone: address.phone,
        default_shipment: address.defaultShipment,
        invoice_buyer: address.invoiceBuyer,
        invoice_recipient: address.invoiceRecipient
      }
      return this._vm.$http.post(this.getters.getApiUrl('addresses'), data, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          this.dispatch('aMessageDisplay', 'user-address-change-success')
        })
        .catch({})
    },

    // cart
    aChangeCartAmount ({ commit, dispatch, getters }, data) {
      let cartId = getters.getCartId || null
      let payload = {
        price_list: data.priceList,
        amount: data.amount
      }
      if (cartId) {
        payload.cart_id = cartId
      }
      return this._vm.$http.post(getters.getApiUrl('cart-items'), payload, {
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          if (!cartId) {
            commit('mSetCartId', result.cart_id)
            dispatch('aFetchCart')
          } else {
            dispatch('aFetchCartTotal')
          }
        })
        .catch((err) => {
          dispatch('aMessageDisplay', err.body.detail)
        })
    },
    aFetchCartItems ({ commit, getters }) {
      return this._vm.$http.get(getters.getApiUrl('cart-items'), {
        params: { page_size: 1000 },
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetCartItems', result.data)
        })
        .catch(() => {
          this.dispatch('aMessageDisplay', 'error-receive-cart-data')
        })
    },
    aFetchCartTotal ({ commit, getters }) {
      if (getters.getCartId) {
        return this._vm.$http.get(getters.getApiUrl('cart-total'), {
          headers: getters.getRequestHeaders()
        })
          .then(response => response.json())
          .then(result => {
            commit('mSetCartTotal', result)
          })
      }
    },
    aFetchCarts: function ({ commit, getters }) {
      return this._vm.$http.get(getters.getApiUrl('carts'), {
        params: { page_size: 100 },
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetCarts', { carts: result.data })
        })
    },
    aSetCartTimestamp: function ({ commit }) {
      commit('mSetCartTimestamp')
    },
    aSetCartStatus: function ({ commit, dispatch, getters }, status) {
      let cartId = getters.getCartId || null

      if (!cartId) {
        dispatch('aMessageDisplay', 'error-order-empty-cart')
        return
      }

      return this._vm.$http.post(
        getters.getApiUrl('carts', { command: 'status' }),
        { 'status': status },
        { headers: getters.getRequestHeaders() })
        .then(response => response.json())
        .then(result => {
          dispatch('aFetchCart')
        })
    },
    aSwitchCart: function ({ commit }, cartId) {
      return new Promise((resolve, reject) => {
        commit('mSetCartId', cartId)
        resolve()
      })
    },
    aFetchCart: function ({ commit, state }, cartId) {
      if (!cartId) {
        cartId = this.getters.getCartId
      }
      if (!state.compoundsMap.length) {
        this.dispatch('aFetchSubdata', 'categories')
        this.dispatch('aFetchSubdata', 'consistencies')
        this.dispatch('aFetchSubdata', 'informations')
        this.dispatch('aFetchSubdata', 'purities')
        this.dispatch('aFetchSubdata', 'units')
      }
      return this._vm.$http.get(this.getters.getApiUrl('carts', { obj: cartId }), {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetCartTotal', result)
        })
        .catch(() => {
          this.dispatch('aMessageDisplay', 'error-receive-cart-data')
        })
    },
    aDeleteCart: function ({ commit, dispatch, getters }, cartId) {
      if (!cartId) {
        cartId = getters.getCartId
      }
      if (cartId === getters.getCartId) {
        commit('mSetCartId')
        dispatch('aFetchCompounds')
          .then(() => {
            dispatch('aFetchCartTotal')
          })
      }
      return this._vm.$http.delete(getters.getApiUrl('carts', { obj: cartId }), {
        headers: getters.getRequestHeaders()
      })
    },
    aDownloadCartOffer ({ getters }, cartId) {
      if (!cartId) {
        cartId = getters.getCartId
      }
      this._vm.$http.get(getters.getApiUrl('pdf-cart-offer', { obj: cartId, context: 'pdf/' }), {
        headers: getters.getRequestHeaders(),
        responseType: 'arraybuffer'
      })
        .then(response => {
          utils.downloadFile(response, 'oferta-' + cartId.slice(0, 4))
        })
        .catch({})
    },
    // cart messages
    aCartMessageCreate ({ commit }, data) {
      const payload = {
        content: data.content
      }
      return this._vm.$http.post(this.getters.getApiUrl('cart-messages'), payload, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          this.dispatch('aMessageDisplay', 'in-touch-message-create-success')
        })
        .catch({})
    },
    aFetchCartMessages ({ commit }) {
      return this._vm.$http.get(this.getters.getApiUrl('cart-messages'), {
        params: { page_size: 100 },
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetCartMessages', result.data)
        })
        .catch({})
    },

    // compounds
    aFetchCompound ({ commit }, compoundId) {
      return this._vm.$http.get(this.getters.getApiUrl('compounds', { obj: compoundId }), {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          result.controlDetails = true
          commit('mSetCompounds', { compounds: [ result ] })
        })
    },
    aFetchCompounds ({ commit, state }, pageSize = 10) {
      if (!state.compoundsMap.length) {
        this.dispatch('aFetchSubdata', 'categories')
        this.dispatch('aFetchSubdata', 'consistencies')
        this.dispatch('aFetchSubdata', 'informations')
        this.dispatch('aFetchSubdata', 'purities')
        this.dispatch('aFetchSubdata', 'units')
      }
      return this._vm.$http.get(this.getters.getApiUrl('compounds'), {
        params: {
          q: this.state.q,
          page_size: pageSize,
          page: this.state.paginationState.page
        },
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mFetchCompounds', { compounds: result.data })
          commit('mSetPagination', { pagination: result.meta })
        })
        .catch()
    },
    aFetchSubdata ({ commit }, key) {
      return this._vm.$http.get(this.getters.getApiUrl(`compounds-${key}`), {
        params: {
          page_size: 1000
        },
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetCompoundsSubdata', { key, data: result.data })
        })
    },
    aSetPage ({ commit }, page) {
      commit('mSetPage', page)
    },

    // content
    aFetchContent: function ({ commit, getters }, category) {
      return this._vm.$http.get(getters.getApiUrl('content-articles'), {
        params: {
          category,
          page_size: 50
        },
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetContent', result.data)
        })
    },
    aFetchSingleContent: function ({ commit, getters }, slug) {
      return this._vm.$http.get(getters.getApiUrl('content-articles', { 'obj': slug }), {
        headers: getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          commit('mSetContent', [ result ])
        })
    },

    // user
    aLogin ({ commit }, { username, password, vm }) {
      let formData = new FormData()
      formData.append('username', username)
      formData.append('password', password)
      formData.append('client_id', clientId)
      formData.append('grant_type', 'password')

      const extraHeaders = {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
      return Vue.http.post(this.getters.getApiUrl('login', { context: 'auth' }), formData, {
        headers: this.getters.getRequestHeaders(extraHeaders)
      })
        .then(response => response.json())
        .then(result => {
          commit('mLogin', result)
          if (vm.$route.params.nextUrl != null) {
            router.push(vm.$route.params.nextUrl)
          } else {
            router.push({ name: 'homepage' })
          }
        }).catch((err) => {
          console.warn('loginError: %o', err)
        })
    },
    aLogout ({ commit }) {
      commit('mLogout')
      router.push({ name: 'homepage' })
    },

    // notification messages
    aMessageDisplay ({ commit }, message) {
      let msg = {}
      if (typeof message === 'string') {
        message = this.state.messagesMap[message] || { message, type: 'error' }
      }
      msg = { ...msg, ...this.state.messageBase, ...message }
      this._vm.$message(msg)
    },

    // intouch messages
    aIntouchMessageCreate ({ commit }, data) {
      const payload = {
        meta: {
          name: data.fullName,
          email: data.email,
          phone: data.phone
        },
        content: data.content,
        title: data.title
      }
      return this._vm.$http.post(this.getters.getApiUrl('intouch-messages'), payload, {
        headers: this.getters.getRequestHeaders()
      })
        .then(response => response.json())
        .then(result => {
          this.dispatch('aMessageDisplay', 'in-touch-message-create-success')
        })
        .catch({})
    }
  },
  mutations: {
    // account
    mSetAccount (state, account) {
      state.account = { ...state.account, ...utils.keysToCamel(account) }
    },

    // addresses
    mSetAddresses (state, addresses) {
      state.addresses = utils.keysToCamel(addresses)
    },

    // cart
    mSetCartId (state, cartId) {
      if (cartId) {
        if (cartId !== this.getters.getCartId) {
          state.cart = {}
          state.cart.cartId = cartId
          sessionStorage.setItem('cartId', cartId)
        }
      } else {
        state.cart = {
          created: null,
          itemCount: 0,
          isOffer: false,
          name: null,
          price: 0,
          status: 'opened',
          statuses: []
        }
        sessionStorage.removeItem('cartId')
        this.commit('mSetCartTimestamp')
      }
    },
    mSetCartItems (state, items) {
      state.cartItems = utils.keysToCamel(items)
    },
    mSetCartTimestamp: function (state) {
      state.cart.timestamp = new Date().getTime()
    },
    mSetCartTotal (state, cart) {
      state.cart = { ...state.cart, ...utils.keysToCamel(cart) }
    },
    mSetCarts (state, { carts }) {
      state.carts = utils.keysToCamel(carts)
    },
    // cart messages
    mSetCartMessages (state, messages) {
      state.cartMessages = messages
    },

    // compounds
    mFetchCompounds (state, { compounds }) {
      state.compounds = utils.keysToCamel(compounds)
      this.commit('mSetCompounds', { compounds })
    },
    mSetCompounds (state, { compounds }) {
      if (compounds) {
        compounds.forEach(compound => {
          if (state.compoundsMap[compound.id]) {
            state.compoundsMap[compound.id] = {
              ...state.compoundsMap[compound.id],
              ...utils.keysToCamel(compound)
            }
          } else {
            state.compoundsMap[compound.id] = utils.keysToCamel(compound)
          }
        })
      }
    },
    mSetCompoundsSubdata (state, { key, data }) {
      if (data) {
        data.forEach((item) => {
          state.compoundsMap[key][item.id] = utils.keysToCamel(item)
        })
      }
    },

    // content
    mSetContent (state, data) {
      if (data) {
        data.forEach((item) => {
          state.contentMap[item.id] = item
          state.contentSlugMap[item.slug] = item.id
          item.categories.forEach((category) => {
            if (!(category.slug in state.categoryMap)) {
              state.categoryMap[category.slug] = new Set()
            }
            state.categoryMap[category.slug].add(item.id)
          })
        })
      }
    },

    mLogin (state, tokenResponse) {
      Vue.set(sessionStorage, 'tkn', tokenResponse['access_token'])
      sessionStorage.setItem('tkn', tokenResponse['access_token'])
      sessionStorage.setItem('tknRefresh', tokenResponse['refresh_token'])
      // state.isAuthenticated = true
      state.token = tokenResponse['access_token']
    },
    mLogout (state) {
      // clean up tokens:
      sessionStorage.removeItem('tkn')
      sessionStorage.removeItem('tknRefresh')
      // clean up cart:
      this.commit('mSetCartId', null)
      state.token = null
    },

    // pagination
    mSetPagination (state, { pagination }) {
      state.paginationState = { ...state.paginationState, ...pagination }
    },
    mSetPage (state, page) {
      state.paginationState.currenPage = page
      state.paginationState.page = state.paginationState.currenPage
    },

    // q (search-box)
    mUpdateQ (state, q) {
      state.q = q
      this.commit('mSetPage', 1)
    }
  },

  getters: {
    getApiUrl: (state) => (endpoint, params = { context: '', obj: '', command: '' }) => {
      if (!params.context) params.context = 'api/'
      if (!params.obj) params.obj = ''
      if (!params.command) params.command = ''
      return utils.urlJoin(params.context, state.apiEndpoints[endpoint], params.obj, params.command, '/')
    },
    getCart: (state) => {
      return state.cart
    },
    getCartId: (state) => {
      if (!state.cart.cartId) {
        return sessionStorage.getItem('cartId')
      }
      return state.cart.cartId
    },
    getCartItems: (state) => (cartId) => {
      return state.cart.items
    },
    getCartItemsCount: (state) => {
      return state.itemCount
    },
    getCartStatusLabel: (state) => (status) => {
      try {
        return state.cartStatuses[status].label
      } catch (err) {
        return status
      }
    },
    getPageTitle: (state) => (suffix = '') => {
      if (suffix) {
        return state.pageTitle + ' | ' + suffix
      }
      return state.pageTitle
    },
    getRequestHeaders: (state, getters) => (extraHeaders) => {
      let headers = {
        'Accept-Language': '*'
      }
      let token = getters.getToken
      let cartId = getters.getCartId

      if (cartId) {
        Object.assign(headers, { 'X-cart': cartId })
      }
      if (token) {
        Object.assign(headers, { 'Authorization': 'Bearer ' + token })
      }
      if (extraHeaders) {
        Object.assign(headers, extraHeaders)
      }
      return headers
    },
    getToken: (state) => {
      if (state.token) {
        return state.token
      }
      return sessionStorage.getItem('tkn')
    },
    isAuthenticated: (state) => {
      if ((!state.token) && (sessionStorage.getItem('tkn'))) {
        state.token = sessionStorage.getItem('tkn')
      }
      if (state.token) {
        return true
      }
      return false
    }
  }
})



// WEBPACK FOOTER //
// ./src/store/store.js